/**
 * Briefing estructurado de proyecto — secciones, análisis de Nexus y propuestas.
 * Source of truth = API (tolerante a respuestas incompletas mientras el backend
 * despliega el contrato, REQ-046c). NUNCA se inventan datos: lo ausente es ''.
 *
 * Endpoints:
 *   GET  /work/projects/:id/briefing          → briefing actual
 *   PUT  /work/projects/:id/briefing          → guarda secciones (sube versión)
 *   POST /work/projects/:id/briefing/analyze  → análisis de Nexus (propuestas)
 *   POST /work/projects/:id/briefing/proposals/:proposalId/confirm → crea trabajo SOLO de lo seleccionado
 */
import { api } from '../utils/api';
import type { TaskPriority } from './workApi';

/* ── Secciones del briefing (UI en español) ─────────────────── */
export interface BriefingSectionDef {
  key: BriefingSectionKey;
  label: string;
  hint: string;
}
export type BriefingSectionKey =
  | 'objective'
  | 'description'
  | 'scope'
  | 'context'
  | 'client'
  | 'instructions'
  | 'restrictions'
  | 'deliverables'
  | 'documentation'
  | 'notes'
  | 'keyCriteria';

export const BRIEFING_SECTIONS: BriefingSectionDef[] = [
  { key: 'objective', label: 'Objetivo', hint: 'Qué resultado busca el proyecto' },
  { key: 'description', label: 'Descripción', hint: 'Descripción general del proyecto' },
  { key: 'scope', label: 'Alcance', hint: 'Qué incluye y qué queda fuera' },
  { key: 'context', label: 'Contexto', hint: 'Antecedentes y situación actual' },
  { key: 'client', label: 'Cliente', hint: 'Quién es el cliente y cómo se relaciona' },
  { key: 'instructions', label: 'Instrucciones', hint: 'Cómo debe trabajarse el proyecto' },
  { key: 'restrictions', label: 'Restricciones', hint: 'Límites, prohibiciones y condiciones' },
  { key: 'deliverables', label: 'Entregables', hint: 'Qué debe producirse' },
  { key: 'documentation', label: 'Documentación', hint: 'Documentos y referencias disponibles' },
  { key: 'notes', label: 'Notas', hint: 'Observaciones libres' },
  { key: 'keyCriteria', label: 'Criterios importantes', hint: 'Criterios que no se pueden negociar' },
];

export const BRIEFING_SECTION_KEYS: BriefingSectionKey[] = BRIEFING_SECTIONS.map((s) => s.key);

/** Alias en español aceptados al leer respuestas del backend. */
const SPANISH_ALIAS: Record<BriefingSectionKey, string> = {
  objective: 'objetivo',
  description: 'descripcion',
  scope: 'alcance',
  context: 'contexto',
  client: 'cliente',
  instructions: 'instrucciones',
  restrictions: 'restricciones',
  deliverables: 'entregables',
  documentation: 'documentacion',
  notes: 'notas',
  keyCriteria: 'importantCriteria',
};

export type BriefingSections = Record<BriefingSectionKey, string>;

export const emptySections = (): BriefingSections =>
  Object.fromEntries(BRIEFING_SECTION_KEYS.map((k) => [k, ''])) as BriefingSections;

export interface ProjectBriefing {
  projectId: string;
  /** 0 = sin briefing guardado todavía. */
  version: number;
  updatedAt: string | null;
  sections: BriefingSections;
}

/* ── Propuestas del análisis de Nexus ──────────────────────── */
export type ProposalType = 'PROCESS' | 'TASK' | 'PERIODIC' | 'RECURRING' | 'DOD' | 'CAPABILITY';

export const PROPOSAL_TYPE_LABELS: Record<ProposalType, string> = {
  PROCESS: 'Proceso',
  TASK: 'Tarea',
  PERIODIC: 'Periódica',
  RECURRING: 'Recurrente',
  DOD: 'DoD',
  CAPABILITY: 'Capacidad',
};
export const PROPOSAL_TYPES: ProposalType[] = ['PROCESS', 'TASK', 'PERIODIC', 'RECURRING', 'DOD', 'CAPABILITY'];

export interface BriefingProposal {
  id: string;
  type: ProposalType;
  title: string;
  description: string;
  priority: TaskPriority | string;
  suggestedAssignee: { id: string; name: string } | null;
  dueDate: string | null;
  estimatedMinutes: number | null;
}

export interface BriefingAnalysis {
  briefingVersion: number;
  proposals: BriefingProposal[];
  notes: string | null;
}

/** Propuesta ya editada por la persona antes de confirmar. */
export interface BriefingProposalConfirm {
  id: string;
  type: ProposalType;
  title: string;
  description: string;
  priority: string;
  assigneeId: string | null;
  /** Nombre libre editable en el panel (null si no cambia el sugerido). */
  assigneeName?: string | null;
  dueDate: string | null;
  estimatedMinutes: number | null;
}

export interface ConfirmResultTask {
  id: string;
  title: string;
  taskCode: string | null;
  /** Trazabilidad: siempre «Origen: Briefing vN». */
  origin: string;
}

export interface BriefingConfirmResult {
  briefingVersion: number;
  tasks: ConfirmResultTask[];
  /** Procesos creados por la confirmación. */
  processes?: { id: string; name: string }[];
  /** Propuestas seleccionadas que NO se convirtieron en trabajo (con motivo). Nunca se ocultan. */
  skipped?: { index: number; type: string; reason: string }[];
}

/* ── Normalización tolerante ───────────────────────────────── */
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

function normalizeSections(raw: Record<string, unknown> | null | undefined): BriefingSections {
  const out = emptySections();
  const src = (raw ?? {}) as Record<string, unknown>;
  const nested = (src.sections ?? {}) as Record<string, unknown>;
  for (const key of BRIEFING_SECTION_KEYS) {
    const v = nested[key] ?? src[key] ?? src[SPANISH_ALIAS[key]] ?? (key === 'keyCriteria' ? src.criterios : undefined);
    out[key] = str(v);
  }
  return out;
}

export function normalizeBriefing(projectId: string, raw: unknown): ProjectBriefing {
  const r = (raw ?? {}) as Record<string, unknown>;
  const version = typeof r.version === 'number' ? r.version : typeof r.briefingVersion === 'number' ? r.briefingVersion : 0;
  return {
    projectId,
    version,
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : null,
    sections: normalizeSections(r),
  };
}

function normalizeProposal(raw: unknown): BriefingProposal {
  const r = (raw ?? {}) as Record<string, unknown>;
  const assignee = (r.suggestedAssignee ?? r.assignee ?? null) as { id: string; name: string } | null; // assigneeSuggestion (solo nombre) no es una asignación: se ignora
  return {
    id: str(r.id),
    type: (PROPOSAL_TYPES.includes(r.type as ProposalType) ? r.type : 'TASK') as ProposalType,
    title: str(r.title),
    description: str(r.description),
    priority: str(r.priority) || 'MEDIUM',
    suggestedAssignee: assignee && assignee.id ? { id: assignee.id, name: str(assignee.name) } : null,
    dueDate: typeof r.dueDate === 'string' ? r.dueDate : null,
    estimatedMinutes: typeof r.estimatedMinutes === 'number' ? r.estimatedMinutes : null,
  };
}

export function normalizeAnalysis(raw: unknown): BriefingAnalysis {
  const r = (raw ?? {}) as Record<string, unknown>;
  const proposals = Array.isArray(r.proposals) ? r.proposals.map(normalizeProposal) : [];
  return {
    briefingVersion: typeof r.briefingVersion === 'number' ? r.briefingVersion : typeof r.version === 'number' ? r.version : 0,
    proposals,
    notes: typeof r.notes === 'string' ? r.notes : null,
  };
}

export function normalizeConfirmResult(raw: unknown, briefingVersion: number): BriefingConfirmResult {
  const r = (raw ?? {}) as Record<string, unknown>;
  const tasks = (Array.isArray(r.tasks) ? r.tasks : []).map((t) => {
    const x = (t ?? {}) as Record<string, unknown>;
    const version = typeof x.briefingVersion === 'number' ? x.briefingVersion : briefingVersion;
    return {
      id: str(x.id),
      title: str(x.title),
      taskCode: typeof x.taskCode === 'string' ? x.taskCode : null,
      origin: `Origen: Briefing v${version}`,
    };
  });
  return {
    briefingVersion: typeof r.briefingVersion === 'number' ? r.briefingVersion : briefingVersion,
    tasks,
    processes: Array.isArray(r.processes) ? (r.processes as { id: string; name: string }[]) : [],
    skipped: Array.isArray(r.skipped) ? (r.skipped as { index: number; type: string; reason: string }[]) : [],
  };
}

/* ── Cliente API ───────────────────────────────────────────── */
export interface BriefingApi {
  get: (projectId: string) => Promise<ProjectBriefing>;
  save: (projectId: string, sections: BriefingSections) => Promise<ProjectBriefing>;
  analyze: (projectId: string) => Promise<BriefingAnalysis>;
  confirm: (projectId: string, proposals: BriefingProposalConfirm[], briefingVersion: number) => Promise<BriefingConfirmResult>;
}

/** Secciones del cliente → campos del servidor (keyCriteria = importantCriteria; '' limpia el campo). */
const toServer = (sections: BriefingSections): Record<string, string> => {
  const { keyCriteria, ...rest } = sections;
  return { ...rest, importantCriteria: keyCriteria };
};

export const briefingApi: BriefingApi = {
  get: async (projectId) => normalizeBriefing(projectId, (await api.get(`/work/projects/${projectId}/briefing`)).data),
  save: async (projectId, sections) =>
    normalizeBriefing(projectId, (await api.put(`/work/projects/${projectId}/briefing`, toServer(sections))).data),
  analyze: async (projectId) => normalizeAnalysis((await api.post(`/work/projects/${projectId}/briefing/analyze`, {})).data),
  confirm: async (projectId, proposals, briefingVersion) => {
    // Los ids de propuesta son «<proposalId>:<índice>»; el servidor confirma por índice y admite ediciones.
    const parsed = proposals.map((p) => ({ p, proposalId: p.id.split(':')[0], index: Number(p.id.split(':')[1]) }));
    const proposalId = parsed[0]?.proposalId ?? '';
    const edits = Object.fromEntries(parsed.map(({ p, index }) => [String(index), {
      title: p.title, description: p.description, priority: p.priority,
      assigneeId: p.assigneeId, dueDate: p.dueDate, estimatedMinutes: p.estimatedMinutes,
    }]));
    const body = { selectedItems: parsed.map((x) => x.index), edits };
    return normalizeConfirmResult((await api.post(`/work/projects/${projectId}/briefing/proposals/${proposalId}/confirm`, body)).data, briefingVersion);
  },
};
